import { useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { CopyText } from '@components/copy-text-field';
import { StateTabs } from '@components/header/state-tabs';
import { IconFormIndicatorInfoCurrentColor } from '@components/icons/form-indicator-info-current-color';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconUrl } from '@components/icons/url';
import { InfoBox } from '@components/info-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { useAuthenticatedUser } from 'features/authentication';
import { Modal } from 'features/modals';
import { sharingSiteUrl } from 'features/routing';
import { useStore } from 'models/Provider';

import { SocialShareButtons } from './SocialShareButtons';

/** @param {{ closeModal: () => void; job: import('models/types').IJob }} props */
const ShareJobModalContent = ({ closeModal, job }) => {
  job.load();
  const { t } = useStore();

  const [shareType, setShareType] = useState('direct');
  const isReferral = shareType === 'referral';
  const me = useAuthenticatedUser();
  const url = sharingSiteUrl(me, isReferral, job.id);

  if (!job) {
    return null;
  }

  return (
    <Modal>
      <Modal.Header text={t('sharejob.header')} />
      <div>
        <StateTabs
          ariaLabel={t('sharejob.tabs.label')}
          activeTab={shareType}
          onTabChange={setShareType}
          tabs={[
            { id: 'direct', label: t('sharejob.selected.tab.direct'), icon: <IconUrl /> },
            {
              id: 'referral',
              label: t('sharejob.selected.tab.referral'),
              icon: <IconPersonAdd />,
            },
          ]}
        />
        <Separator />
      </div>
      {isReferral && (
        <InfoBox
          variant="indigo"
          icon={<IconFormIndicatorInfoCurrentColor />}
          text={t('sharejob.label')}
        />
      )}
      {job && job.hasRewards && isReferral && (
        <Stack space={6}>
          <Text font="heading2" color="purple100" text={t('sharejob.rewards.header')} />
          <div>
            <Text font="bold16" text={job.rewardsTitle} />
            <Text text={job.rewards} />
          </div>
        </Stack>
      )}
      <Stack space={1}>
        <CopyText
          copyText={t('copy.button.text')}
          copiedText={t('copy.button.copied')}
          label={t('sharejob.copy.header.' + (isReferral ? 'referral' : 'direct'))}
          value={url}
        />
      </Stack>

      <Separator />

      <Text font="heading2" color="purple100" text={t('sharejob.header.sharing.title')} />
      <SocialShareButtons url={url} />
      <Modal.Actions>
        <Button variant="secondary" onClick={closeModal} text={t('sharejob.modal.close')} />
      </Modal.Actions>
    </Modal>
  );
};

export const ShareJobModal = observer(ShareJobModalContent);
